import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Test } from '@bugbug/core/types/tests';
import { useAppSelector } from '~/modules/store';
import { selectSingleTestRunId } from '~/modules/testRun/testRun.selectors';
import { selectPlaybackCursorPosition } from '~/modules/uiState/uiState.selectors';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { selectCurrentTest, selectCurrentTestGroups } from '../../tests.selectors';
import { showPendingGroup } from '../../tests.utils';
import { usePlaybackCursorPosition } from '../usePlaybackCursorPosition/usePlaybackCursorPosition';

export const useSplitGroup = () => {
  const [splitGroup, splitGroupState] = testsApi.useSplitGroupMutation();
  const test = useAppSelector(selectCurrentTest);
  const groups = useAppSelector(selectCurrentTestGroups);
  const cursorPosition = useAppSelector(selectPlaybackCursorPosition);
  const testRunId = useAppSelector(selectSingleTestRunId);
  const { refreshPlaybackCursorPosition } = usePlaybackCursorPosition();

  const handleSplitGroup = useCallback(
    async (params: {
      id: Group['id'];
      testId: Test['id'];
      atIndex: number;
      name?: Group['name'];
    }) => {
      const pendingToast = showPendingGroup(ACTIONS.SPLIT);
      const stepsIds = groups[params.id].steps.slice(params.atIndex);

      const result = splitGroup({
        name: params.name ?? null,
        id: params.id,
        testId: params.testId,
        stepsIds,
        stepAtIndex: params.atIndex,
        atIndex: (test?.groups?.indexOf(params.id) ?? -1) + 1,
        testRunId: testRunId ?? null,
      }).unwrap();

      result
        .then((splitChangesMap) => {
          if (cursorPosition?.afterStep?.groupId === params.id) {
            const [sourceGroup, createdGroup] = splitChangesMap[params.id];
            const newAfterStep = {
              ...cursorPosition.afterStep,
              groupId: stepsIds.includes(cursorPosition.afterStep.id as string)
                ? createdGroup.id
                : sourceGroup.id,
            };
            refreshPlaybackCursorPosition({ step: newAfterStep });
          }
          pendingToast.success();
        })
        .catch((error) => {
          // @ts-expect-error Remove this comment after migrating `internalServerError` to TypeScript
          const shouldShowRequestError = pendingToast.shouldShowRequestError(error);
          if (shouldShowRequestError) {
            pendingToast.error();
          } else {
            pendingToast.dismiss();
          }
        });

      return result;
    },
    [
      groups,
      splitGroup,
      test?.groups,
      cursorPosition?.afterStep,
      refreshPlaybackCursorPosition,
      testRunId,
    ],
  );

  return useMemo(
    () => [handleSplitGroup, splitGroupState] as const,
    [handleSplitGroup, splitGroupState],
  );
};
